/**
 * Customer testimonials — single source of truth.
 *
 * Rendered on /reviews and marked up as schema.org Review + AggregateRating
 * via ReviewStructuredData. Keeping the data here ensures the on-page reviews
 * and the structured data can never disagree on count or rating.
 */

export interface Testimonial {
  name: string;
  role: string;
  companyType: string;
  content: string;
  rating: number;
  switchedFrom?: string;
}

export const testimonials: Testimonial[] = [
  {
    name: 'Sarah M.',
    role: 'Founder',
    companyType: 'E-commerce startup',
    content:
      'I was drowning in AI-generated partnership pitches. Email Ferret catches every single one and my inbox is finally manageable. The scoring breakdown is brilliant — I can see exactly why something was flagged.',
    rating: 5,
    switchedFrom: 'Switched from SaneBox',
  },
  {
    name: 'James K.',
    role: 'VP Engineering',
    companyType: 'Series B SaaS',
    content:
      'We rolled this out to our entire engineering team. The smart folders alone saved us hours per week — recruiting emails, vendor pitches, and GitHub notifications all sorted automatically.',
    rating: 5,
    switchedFrom: 'Switched from manual Gmail filters',
  },
  {
    name: 'Priya R.',
    role: 'Head of Recruiting',
    companyType: 'Tech recruiting firm',
    content:
      'As a recruiter, I get hundreds of cold emails from tool vendors daily. Email Ferret filters out the noise while keeping real candidate and client emails front and center. Game changer.',
    rating: 5,
    switchedFrom: 'Switched from Mailstrom',
  },
  {
    name: 'David L.',
    role: 'CTO',
    companyType: 'AI startup',
    content:
      'The irony of using AI to catch AI-generated spam is not lost on me. But it works incredibly well. The privacy-first approach sealed the deal — no email body storage is exactly what we needed.',
    rating: 5,
  },
  {
    name: 'Rachel T.',
    role: 'Marketing Director',
    companyType: 'Growth-stage fintech',
    content:
      'I was skeptical at first, but after the 14-day trial I was hooked. It caught cold outreach that looked completely legitimate — personalized subject lines, my company name, the whole nine yards.',
    rating: 5,
    switchedFrom: 'Switched from Clean Email',
  },
  {
    name: 'Marcus W.',
    role: 'Solo Consultant',
    companyType: 'Independent consulting',
    content:
      'At $5/month, this is the best ROI of any tool I use. I used to spend 20 minutes every morning sorting through junk. Now my inbox has only emails I actually need to read.',
    rating: 5,
  },
  {
    name: 'Anika S.',
    role: 'Product Manager',
    companyType: 'Enterprise SaaS',
    content:
      'The allowlist and blocklist features give me fine-grained control. I whitelisted a few vendors I actually work with, and everything else gets filtered perfectly. Zero false positives in three months.',
    rating: 5,
    switchedFrom: 'Switched from SaneBox',
  },
  {
    name: 'Tom H.',
    role: 'Investor & Board Member',
    companyType: 'Venture capital',
    content:
      'I get cold outreach from 50+ startups a day. Email Ferret keeps my inbox clean so I only see deal flow from trusted sources and portfolio companies. The auto-organization into folders is a massive time saver.',
    rating: 5,
  },
];

/** Number of on-page testimonials marked up as Review items. */
export const REVIEW_COUNT = testimonials.length;

/** Average rating across the on-page testimonials, rounded to one decimal. */
export const REVIEW_AGGREGATE_RATING =
  Math.round((testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length) * 10) / 10;
